import { Command } from "@ckeditor/ckeditor5-core";
import { Plugin } from "@ckeditor/ckeditor5-core";
import { ModelPosition as Position } from "@ckeditor/ckeditor5-engine";
import { ModelTreeWalker as TreeWalker } from "@ckeditor/ckeditor5-engine";
import { ButtonView } from "@ckeditor/ckeditor5-ui";

import CreateHighlightIcon from "./marker.svg";

import "./Highlight.css";

const applyHighlight = (writer) => (model) => (active) => (highlight) => {
  if (highlight?.__typename === "Highlight") {
    const [start, finish] = getBoundaries(model.document.getRoot())(highlight);
    if (!start || !finish) return;
    const ranges = model.schema.getValidRanges(
      [writer.createRange(start, finish)],
      "cdeHighlight",
    );
    for (const range of ranges) {
      // A single attribute holds both the title and the active flag, so every
      // text range resolves to exactly one color. Inactive highlights are
      // applied first and active ones last (see DisplayItemHighlights), so this
      // overwrite makes the active highlight win wherever ranges overlap/nest.
      writer.setAttribute(
        "cdeHighlight",
        { active, title: highlight.title },
        range,
      );
    }
  }
  if (highlight?.__typename === "ItemPointReference") {
    const root = model.document.getRoot();
    const startPosition = new Position(root, [0]);
    const walker = new TreeWalker({ startPosition, ignoreElementEnd: true });

    for (const element of walker) {
      if (
        element.item.name === "itemPointReference" &&
        element.item.getAttribute("data-uuid") === highlight?.point
      ) {
        writer.removeAttribute("iprActiveHighlight", element.item);
        writer.removeAttribute("iprHighlight", element.item);
        writer.setAttribute(
          active ? "iprActiveHighlight" : "iprHighlight",
          "true",
          element.item,
        );
        writer.setAttribute("title", highlight.title, element.item);
      }
    }
  }
};
const getBoundaries = (root) => (highlight) => {
  const startPosition = new Position(root, [0]);
  const walker = new TreeWalker({ startPosition });
  let start, finish;
  for (const element of walker) {
    if (!start && isBeginHLElement(highlight.begin)(element)) {
      start = element.nextPosition;
    }
    if (start && isEndHLElement(highlight.end)(element)) {
      finish = element.previousPosition;
      break;
    }
  }
  if (!start) console.warn("Missing start position", highlight);
  if (!finish) console.warn("Missing finish position", highlight);
  return [start, finish];
};
const isBeginHLElement = (uuid) => (element) => {
  return (
    element.type === "elementEnd" &&
    element.item.name === "inlineMarkElement" &&
    element.item.getAttribute("data-uuid") === uuid
  );
};
const isEndHLElement = (uuid) => (element) => {
  return (
    element.type === "elementStart" &&
    element.item.name === "inlineMarkElement" &&
    element.item.getAttribute("data-uuid") === uuid
  );
};
const removeHighlightAttributes = (model) => (writer) => {
  const root = model.document.getRoot();
  const startPosition = new Position(root, [0]);
  const endPosition = new Position(root, [root.maxOffset]);
  const ranges = model.schema.getValidRanges(
    [writer.createRange(startPosition, endPosition)],
    "cdeHighlight",
  );
  for (const range of ranges) {
    writer.removeAttribute("cdeHighlight", range);
  }
};

// A highlight renders active when it is the one being hovered (hl), or — when
// nothing is hovered — when it belongs to the item currently being edited.
const matchesHoveredHighlight = (hl) => (highlight) =>
  (highlight.__typename === "Highlight" &&
    highlight.begin === hl.begin &&
    highlight.end === hl.end) ||
  (highlight.__typename === "ItemPointReference" &&
    highlight.point === hl.point);

const isActiveHighlight = (hl) => (itemID) => (highlight) => {
  if (hl) return matchesHoveredHighlight(hl)(highlight);
  if (itemID) return highlight.items.includes(itemID);
  return false;
};

class DisplayItemHighlights extends Command {
  execute(highlights) {
    const command = this;
    return function (hl) {
      const itemID = command.editor.config.get("itemID");
      const [activeHighlights, inactiveHighlights] = highlights.reduce(
        ([activeAcc, inactiveAcc], highlight) =>
          isActiveHighlight(hl)(itemID)(highlight)
            ? [[...activeAcc, highlight], inactiveAcc]
            : [activeAcc, [...inactiveAcc, highlight]],
        [[], []],
      );
      const model = command.editor.model;
      const editing = command.editor.editing;
      // Re-derive the model attributes: clear every cdeHighlight, then apply
      // inactive highlights before active ones so the active value wins wherever
      // ranges overlap/nest.
      model.change((writer) => {
        removeHighlightAttributes(model)(writer);
        inactiveHighlights.map(applyHighlight(writer)(model)(false));
        activeHighlights.map(applyHighlight(writer)(model)(true));
      });
      // Force a full re-downcast of every block that contains marks. The
      // differ's incremental attribute conversion fails to re-wrap a <span> for
      // text that sits between empty <mark> elements when a nested highlight
      // transitions active -> inactive: the word renders with no span at all
      // (white), and stays white because the steady-state model then converts
      // identically on every later hover, so there is no diff to repair the DOM.
      // Reconverting the block rebuilds all of its highlight spans from the
      // model in one shot, so nested/overlapping highlights always resolve to
      // the correct color.
      const root = model.document.getRoot();
      for (const block of root.getChildren()) {
        if (!block.is("element")) continue;
        const hasMark = [...model.createRangeIn(block).getItems()].some(
          (item) =>
            item.is("element", "inlineMarkElement") ||
            item.is("element", "itemPointReference"),
        );
        if (hasMark) editing.reconvertItem(block);
      }
    };
  }

  constructor(editor) {
    super(editor);
    this.affectsData = false;
  }
}

export default class HighlightUI extends Plugin {
  init() {
    const editor = this.editor;
    const t = editor.t;

    editor.commands.add(
      "displayItemHighlights",
      new DisplayItemHighlights(editor),
    );

    editor.ui.componentFactory.add("createHighlight", (locale) => {
      const command = editor.commands.get("createMarks");
      const buttonView = new ButtonView();

      buttonView.set({
        icon: CreateHighlightIcon,
        isOn: false,
        tooltip: true,
      });

      buttonView.bind("isEnabled").to(command);
      buttonView
        .bind("label")
        .to(command, "value", (value) =>
          !value ? "Create Highlight" : "Create Item Point Reference",
        );
      buttonView.on("execute", (e) => {
        command.execute("highlight");
      });
      return buttonView;
    });
  }
}
